import React, { ReactElement, useEffect, useState } from "react";

import clsx from "clsx";
import { startCase } from "lodash";
import { ConnectedProps, connect, useDispatch } from "react-redux";

import { Grid, Theme, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridCellParams, GridColDef, GridRenderCellParams, GridSelectionModel } from "@mui/x-data-grid";

import { nonce } from "App/App";
import { AppDispatch, RootState } from "App/Store";

import * as constants from "Common/EVaultAppConstants";

import ErrorOverlay from "Components/DataGridCustomComponents/ErrorOverlay";
import LoadingOverlay from "Components/DataGridCustomComponents/LoadingOverlay";
import TableMenuItem from "Components/Table/TableMenuItem";
import TableSelect from "Components/Table/TableSelect";

import { profileSelector } from "Features/Profile/ProfileSlice";
import {
  IMersRecipientDetails,
  IVaultViewClosedLoansSearchResultData,
  RecipientAccessors,
  VaultViewClosedLoansTabTypes,
} from "Features/Vault/VaultInterfaces";
import { datagridColumns } from "Features/Vault/shared";

import localization from "../../localization";
import MersIdentificationNumber from "../MersIdentificationNumber";
import MersRecipientSelector from "../MersRecipientSelector";
import { IMersUpdateProps } from "./MersUpdate";
import { updateChangeType, updateRecipient } from "./MersUpdateSlice";
import { fetchUpdates, updateUpdateQueue } from "./mersUpdateService";

const mapStateToProps = (state: RootState) => ({
  error: state.mersUpdates.error,
  initialized: state.mersUpdates.initialized,
  isLoading: state.mersUpdates.isLoading,
  mersRecipients: state.mersRecipients.recipients,
  settings: profileSelector(state).settings,
});

const connector = connect(mapStateToProps);
type TypesFromRedux = ConnectedProps<typeof connector>;

type Props = IMersUpdateProps & TypesFromRedux;

export function UpdateRightsHolderTable(props: Props): ReactElement {
  const keyRoot = "VaultTab-ClosedLoans-";
  const dispatch = useDispatch() as AppDispatch;
  const classes = useStyles();
  const { error, initialized, isLoading, queueList, settings, updateSet } = props;

  const [selected, setSelected] = useState<GridSelectionModel>(
    queueList.filter((loan) => loan.selected).map((loan) => loan.packageId)
  );

  useEffect(() => {
    if (!initialized) {
      dispatch(fetchUpdates());
    }
  }, [dispatch, initialized]);

  const columns: GridColDef[] = [
    {
      ...datagridColumns.loanNumber,
      sortable: false,
    },
    {
      ...datagridColumns.min,
      renderCell: (params: GridRenderCellParams) => (
        <MersIdentificationNumber
          keyRoot={keyRoot}
          loan={params.row as IVaultViewClosedLoansSearchResultData}
          loanType={constants.CLOSED_LOANS}
          tabKeyRoot={VaultViewClosedLoansTabTypes.MERSInquiryDetails}
          value={params.row.min}
        />
      ),
      sortable: false,
    },
    datagridColumns.primaryBorrowerName,
    {
      cellClassName: (params: GridCellParams) =>
        clsx({
          invalid: params.row.selected && !params.row.rightsholder,
        }),
      field: "updateTypeSelector",
      flex: 1,
      headerName: updateSet.toUpperCase(),
      renderCell: (params: GridRenderCellParams) => {
        const rightsholderOptions: JSX.Element[] = [
          <TableMenuItem key="" value="">
            none
          </TableMenuItem>,
          <TableMenuItem
            key={RecipientAccessors.servicingAgentRecipient}
            value={RecipientAccessors.servicingAgentRecipient}
          >
            {startCase(localization.mers.rightsHolder.servicer)}
          </TableMenuItem>,
        ];

        if (settings.mers.delegateeForTransferEnabled) {
          rightsholderOptions.push(
            <TableMenuItem
              key={RecipientAccessors.delegateeForTransferRecipient}
              value={RecipientAccessors.delegateeForTransferRecipient}
            >
              {localization.mers.rightsHolder.delegateeForTransfersStartCase}
            </TableMenuItem>
          );
        }

        if (settings.mers.securedPartyEnabled) {
          rightsholderOptions.push(
            <TableMenuItem
              key={RecipientAccessors.securedPartyRecipient}
              value={RecipientAccessors.securedPartyRecipient}
            >
              {startCase(localization.mers.rightsHolder.securedParty)}
            </TableMenuItem>
          );

          if (settings.mers.securedPartyDelegateeEnabled) {
            rightsholderOptions.push(
              <TableMenuItem
                key={RecipientAccessors.securedPartyDelegateeRecipient}
                value={RecipientAccessors.securedPartyDelegateeRecipient}
              >
                {startCase(localization.mers.rightsHolder.securedPartyDelegatee)}
              </TableMenuItem>
            );
          }
        }

        return (
          <Grid container item alignItems="center" justifyContent="center" sx={{ px: 1 }}>
            <TableSelect
              displayEmpty={true}
              value={params.row.rightsholder || ""}
              name="rightsholder"
              fullWidth
              onChange={(e) => handleENoteUpdateChange(e, params.row)}
              size="small"
              variant="standard"
            >
              {rightsholderOptions}
            </TableSelect>
          </Grid>
        );
      },
      sortable: false,
    },
    {
      cellClassName: (params: GridCellParams) =>
        clsx({
          invalid:
            params.row.selected &&
            (!params.row.rightsholderRecipient ||
              params.row.rightsholderRecipient.id === constants.EMPTY_MERS_RECIPIENT_DETAILS.id),
        }),
      field: "recipientSelector",
      flex: 1,
      headerName: "RECIPIENT",
      renderCell: (params: GridRenderCellParams) => (
        <Grid container item alignItems="center" justifyContent="center" sx={{ px: 1 }}>
          <MersRecipientSelector
            mersRecipients={props.mersRecipients}
            onMersRecipientIdSelectionChanged={handleRecipientChange}
            currentRecipient={params.row.rightsholderRecipient}
            parentLoan={params.row}
            type={RecipientAccessors.rightsholderRecipient}
          />
        </Grid>
      ),
      sortable: false,
    },
  ];

  function handleENoteUpdateChange(e: any, loan: any) {
    dispatch(
      updateChangeType({
        packageId: loan.packageId,
        property: e.target.name,
        value: e.target.value,
      })
    );
  }

  function handleRecipientChange(recipient: IMersRecipientDetails, loan: any, type?: any) {
    if (type) {
      dispatch(
        updateRecipient({
          packageId: loan.packageId,
          recipient,
          type,
        })
      );
    }
  }

  const onSelectionModelChange = (selected: GridSelectionModel) => {
    setSelected(selected);
    const updates = queueList.map((queue) => {
      return {
        ...queue,
        selected: selected.includes(queue.packageId),
      };
    });
    dispatch(updateUpdateQueue(updates));
  };

  const rightsHolderTable = (
    <DataGrid
      autoHeight
      checkboxSelection
      classes={{
        root: classes.root,
      }}
      columnBuffer={0}
      columns={columns}
      components={{
        ErrorOverlay: ErrorOverlay,
        LoadingOverlay: LoadingOverlay,
      }}
      componentsProps={{
        errorOverlay: { error },
      }}
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableSelectionOnClick
      editMode="row"
      error={error}
      hideFooter
      loading={isLoading}
      localeText={{
        noRowsLabel: "No results found",
      }}
      nonce={nonce}
      onSelectionModelChange={onSelectionModelChange}
      selectionModel={selected}
      rows={queueList}
      scrollbarSize={0}
    />
  );
  return (
    <>
      <Typography>Location changes require Transfer (Location).</Typography>
      {rightsHolderTable}
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    "& .invalid": {
      background: `${theme.palette.error.main}20`,
    },
  },
}));

export default connector(UpdateRightsHolderTable);
